﻿import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl, MatTableDataSource, PageEvent, Sort } from '@angular/material';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Errors, SellerService, LocalService, ManagerService, FileService } from '../../../../core';
import { PopupMessageService } from '../../../../shared/message/services/message.service';
import { sellerApi } from '../../../../config';
import { CustomPaginator } from 'src/app/config/CustomPaginatorConfiguration';
import { SegmentedToggleOption } from '../../../../shared/ui/segmented-toggle/segmented-toggle.component';
import { TabTableConfig } from '../../../../shared/components/expandable-tab-table/expandable-tab-table.component';

@Component({
  selector: 'app-admin-sales-claims',
  templateUrl: './admin-sales-claims.component.html',
  styleUrls: ['./admin-sales-claims.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [
    { provide: MatPaginatorIntl, useFactory: () => CustomPaginator() }
  ],
})
export class AdminSalesClaimsComponent implements OnInit {
  Math = Math;

  errors: Errors = { errors: {} };

  // Profile / role / preference state
  userProfile: any;
  userid: any;
  region = '';
  companybeGeoId = '';
  pamData: any = '';
  disableClaim = false;
  companyClaiming = true;
  distributePointsPreference = { enabled: false };
  adminPreference = { enabled: false };
  companyRewardType = 'POINTS';
  allUsers: any;
  dropdownSettings: any;

  dataLoaded = false;

  // Claim-flow state
  selectedItems: any = {};
  claimAllBtn = false;
  claimEligibleDeal = false;

  // Claim modal state
  claimDisclaimerModalOpen = false;
  claimDisclaimerVariant: 'adminClaimForUser' | 'nonEligibledeal' | null = null;
  claimDisclaimerUser: any = null;
  claimDisclaimerMessage: string = '';
  claimDisclaimerParams: any = null;
  pendingClaimUsers: any[] = [];
  pendingClaimUserIndex: number = 0;
  pendingClaimDealId: any = null;
  claimSuccessModalOpen = false;
  claimSuccessData: any = null;
  successMsg = 'All listed orders were successfully claimed and points issued for distribution.';
  partialSuccessMsg = 'Claiming completed. Not all orders were processed for points because your company budget cap has been reached.';

  // Sales table state
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSalesList = new MatTableDataSource();
  displayedSalesColumnsList: string[] = ['expand', 'name', 'poNumber', 'bookingDate', 'bookingAmount', 'quarter', 'points', 'userName', 'claimedStatus'];
  selectedElementId: any = null;
  filterSearch: any = null;
  eligibleDeal = 'eligible';

  pageSizeOptions: number[] = [5, 10, 25, 50, 100, 300];
  pnDisabled = false;

  queryParamsData: any = {
    pageIndex: 0,
    pageSize: 5,
    sortBy: null,
    sortDirection: null,
    searchText: null
  };
  totalElements: any = null;

  challengeData: { eligibleChallenges: any[]; inEligibleChallenges: any[] } = {
    eligibleChallenges: [],
    inEligibleChallenges: []
  };
  claimsTabConfig: TabTableConfig[] = [];

  showLoader = false;
  dealFilterOptions: SegmentedToggleOption[] = [
    { label: 'Eligible Deals', value: 'eligible' },
    { label: 'All Deals', value: 'all' }
  ];

  statusFilterOptions: any[] = [
    { id: 'all', name: 'All Status' },
    { id: 'true', name: 'Claimed' },
    { id: 'false', name: 'Unclaimed' }
  ];

  selectedStatusFilter: any = { id: 'all', name: 'All Status' };

  constructor(
    private sellerService: SellerService,
    private popupMessageService: PopupMessageService,
    private managerService: ManagerService,
    private fileService: FileService,
  ) {}

  ngOnInit() {
    this.userProfile = LocalService.get('userProfile');
    this.companybeGeoId = this.userProfile.company.beGeoId;
    if (this.userProfile.hasOwnProperty('userType') && this.userProfile.userType === 'PAM') {
      this.pamData = LocalService.get('pamData');
      const beGeoIdsClaim = this.pamData.pamBeGeoIdsDTO ? this.pamData.pamBeGeoIdsDTO.beGeoIds : this.pamData.beGeoIds;
      const firstClaim = (beGeoIdsClaim.content && beGeoIdsClaim.content.length > 0) ? beGeoIdsClaim.content[0] : (beGeoIdsClaim.length > 0 ? beGeoIdsClaim[0] : null);
      if (firstClaim && firstClaim.companyEligibleChallengesDTO && firstClaim.companyEligibleChallengesDTO.pvieligible === false) {
        this.disableClaim = true;
      }
    }
    this.region = this.userProfile.company.region;
    this.userid = this.userProfile.id;
    this.getUsersUnderManager();
    this.multiselectConfig();
    this.loadCompanyPreferences();
    if (
      (this.userProfile.pvieligible === false) ||
      (this.userProfile.companyEligibleChallengesDTO && (this.userProfile.companyEligibleChallengesDTO.pvieligible === false))
    ) {
      this.disableClaim = true;
    }
  }

  getUsersUnderManager() {
    this.managerService.getUsersUnderManager(this.userProfile.id).subscribe(
      (data: any[]) => {
        if (data && data.length) {
          const seenIds = new Set<string>();
          this.allUsers = data
            .filter((item: any) => {
              if (seenIds.has(item.id)) {
                return false;
              }
              seenIds.add(item.id);
              return true;
            })
            .map((item: any) => ({
              id: item.id,
              fullName: `${item.firstName} ${item.lastName}`
            }))
            .sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
        }
      },
      (err) => {
        this.errors = err;
      }
    );
  }

  multiselectConfig() {
    this.dropdownSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'fullName',
    };
  }

  private loadCompanyPreferences(): void {
    if (this.userProfile && this.userProfile.company && this.userProfile.company.id) {
      const companyParams = { companyId: this.userProfile.company.id };
      this.managerService.getCompanyPreferences(companyParams).subscribe(
        (data) => {
          this.distributePointsPreference = data.companyPreferences.find((item: any) => item.id === 8 && item.name === 'COMPANY_DISTRIBUTE_POINTS');
          this.adminPreference = data.companyPreferences.find((item: any) => item.id === 11 && item.name === 'COMPANY_CLAIMER_ADMIN');
          const companyRewardTypeSel = data.companyPreferences.find((item: any) => item.id === 15 && item.name === 'COMPANY_REWARDTYPE_POINTS');
          const rewardTypeCash = data.companyPreferences.find((item: any) => item.id === 16 && item.name === 'COMPANY_REWARDTYPE_CASH');
          if (companyRewardTypeSel.enabled) {
            this.companyRewardType = 'POINTS';
          } else {
            this.companyRewardType = 'CASH';
          }
          if (companyRewardTypeSel.enabled === false && rewardTypeCash.enabled === false) {
            this.companyRewardType = 'POINTS';
          }
          const companyClaim = data.companyPreferences.find((item: any) => item.name === 'COMPANY_CLAIMING');
          this.companyClaiming = companyClaim !== undefined ? companyClaim.enabled : false;
          this.dataLoaded = true;
        },
        (err) => {
          this.errors = err;
          this.dataLoaded = true;
        }
      );
    } else {
      this.dataLoaded = true;
    }
  }

  // ---------------- Sales table data flow ----------------

  searchDataList(searchValue: string) {
    this.filterSearch = searchValue;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.queryParamsData.pageIndex = 0;
    this.getNewSalesChallengeDataList();
  }

  getPaginatorData(event: PageEvent) {
    this.pnDisabled = true;
    this.queryParamsData.pageIndex = event.pageIndex;
    this.queryParamsData.pageSize = event.pageSize;
    this.getNewSalesChallengeDataList();
  }

  sortData(sort: Sort) {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.queryParamsData.pageIndex = 0;
    if (sort.active === 'customerName') {
      this.queryParamsData.sortBy = 'CUSTOMER_NAME';
    }
    if (sort.active === 'bookingDate') {
      this.queryParamsData.sortBy = 'BOOKING_DATE';
    }
    this.queryParamsData.sortDirection = sort.direction.toUpperCase();
    this.getNewSalesChallengeDataList();
  }

  onDealFilterChange(value: string) {
    this.eligibleDeal = value;
    this.getNewSalesChallengeDataList();
  }

  onStatusFilterChange(selectedStatus: any) {
    console.log('status-----',selectedStatus)
    this.selectedStatusFilter = selectedStatus;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.queryParamsData.pageIndex = 0;
    this.getNewSalesChallengeDataList();
  }

  private isEligibleDeals(): boolean {
    return this.eligibleDeal === 'eligible';
  }

  getNewSalesChallengeDataList() {
    let queryParams = `page=${this.queryParamsData.pageIndex}&size=${this.queryParamsData.pageSize}&onlyEligibleDeals=${this.isEligibleDeals()}`;

    if (this.queryParamsData.sortBy) {
      queryParams = queryParams + `&sortBy=${this.queryParamsData.sortBy}&sortDir=${this.queryParamsData.sortDirection}`;
    }

    if (this.filterSearch) {
      queryParams = queryParams + `&searchText=${this.filterSearch}`;
    }

    if (this.selectedStatusFilter && this.selectedStatusFilter.id && this.selectedStatusFilter.id !== 'all') {
      queryParams = queryParams + `&claimStatus=${this.selectedStatusFilter.id}`;
    }

    this.sellerService.getNewSalesChallengeList(this.userProfile.company.id, queryParams).subscribe(
      (data) => {
        this.dataSalesList.data = data.content;
        this.totalElements = data.totalElements;
        this.pnDisabled = false;
      },
      (err) => {}
    );
  }

  // ---------------- Row expansion ----------------

  open(element: any) {
    if (!element.isExpanded) {
      this.selectedElementId = element.id;
      element.isExpanded = !element.isExpanded;
      this.showLoader = true;
      this.sellerService.getChallengeDetails(element.id).subscribe(
        (data) => {
          this.showLoader = false;
          this.challengeData = data;
          this.computeTabConfig();
        },
        (err) => {
          this.showLoader = false;
        }
      );
    } else {
      element.isExpanded = !element.isExpanded;
    }
  }

  private computeTabConfig(): void {
    this.claimsTabConfig = [
      {
        label: 'Eligible',
        icon: 'check_circle',
        iconColor: '#22c55e',
        data: this.challengeData.eligibleChallenges.map((c: any) => ({
          challengeName: c.challengeName,
          id: c.acronym,
          pointsBreakdown: this.companyRewardType === 'POINTS'
            ? c.pointsBreakDown
            : (c.pointsBreakDown / 200).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
        })),
        columns: ['challengeName', 'id', 'pointsBreakdown'],
        columnHeaders: { challengeName: 'Challenge Name', id: 'Id', pointsBreakdown: 'Rewards Breakdown' },
        emptyMessage: 'No eligible challenges found.'
      },
      {
        label: 'Ineligible',
        icon: 'info',
        iconColor: '#9ca3af',
        data: this.challengeData.inEligibleChallenges.map((c: any) => ({
          challengeName: c.challengeName,
          id: c.acronym,
          comments: c.comments
        })),
        columns: ['challengeName', 'id', 'comments'],
        columnHeaders: { challengeName: 'Challenge Name', id: 'Id', comments: 'Comments' },
        emptyMessage: 'No ineligible challenges found.'
      }
    ];
  }

  // ---------------- User-claim flow ----------------

  getDropdownItems(users: any[], elementId: any): any[] {
    if (!users) {
      return [];
    }
    return users.map((user: any) => ({
      label: user.fullName ? user.fullName : 'Unknown',
      value: user,
      checked: this.selectedItems[elementId] && this.selectedItems[elementId].some((item: any) => item.id === user.id) || false
    }));
  }

  onUserSelectionChange(selectedItems: any[], dealId: any): void {
    this.selectedItems[dealId] = selectedItems.map((item: any) => item.value);
  }

  async onConfirmClaim(dealId: any): Promise<void> {
    const selectedUsers = this.selectedItems[dealId];
    if (!selectedUsers || selectedUsers.length === 0) {
      return;
    }
    this.pendingClaimUsers = selectedUsers.slice();
    this.pendingClaimUserIndex = 0;
    this.pendingClaimDealId = dealId;
    this.selectedItems[dealId] = [];
    await this.processNextClaimUser();
  }

  private async processNextClaimUser(): Promise<void> {
    if (this.pendingClaimUserIndex >= this.pendingClaimUsers.length) {
      this.pendingClaimUsers = [];
      this.pendingClaimDealId = null;
      return;
    }
    const user = this.pendingClaimUsers[this.pendingClaimUserIndex];
    this.pendingClaimUserIndex++;
    const isEligible = await this.geteligibledealsData(user, this.pendingClaimDealId);
    if (!isEligible) {
      await this.processNextClaimUser();
      return;
    }
    this.claimDisclaimerParams = {
      userId: user.id,
      dealId: this.pendingClaimDealId,
      quarterOption: 'CURRENT',
      companyId: this.userProfile.company.id
    };
    this.claimDisclaimerUser = user;
    this.claimDisclaimerMessage = '';
    this.claimDisclaimerVariant = this.claimEligibleDeal ? 'adminClaimForUser' : 'nonEligibledeal';
    this.claimDisclaimerModalOpen = true;
  }

  closeClaimDisclaimerModal(): void {
    this.claimDisclaimerModalOpen = false;
    this.processNextClaimUser();
  }

  onClaimDisclaimerConfirm(): void {
    this.claimDisclaimerModalOpen = false;
    this.managerService.adminClaimForUser(this.claimDisclaimerParams).subscribe(
      (data) => {
        this.claimSuccessData = data;
        this.claimSuccessModalOpen = true;
        this.getNewSalesChallengeDataList();
      },
      (err) => {
        this.errors = err;
        if (err.message) {
          this.popupMessageService.showError(err.message, 'error');
        } else {
          this.popupMessageService.showError('Single user cannot claim a deal twice.', 'error');
        }
        this.processNextClaimUser();
      }
    );
  }

  closeClaimSuccessModal(): void {
    this.claimSuccessModalOpen = false;
    this.claimSuccessData = null;
    this.processNextClaimUser();
  }

  claimAll() {
    this.claimAllBtn = true;
    this.managerService.claimAllDeals('?userId=' + this.userid).subscribe(
      (data) => {
        if (data && data.status === 200) {
          this.popupMessageService.showSuccess(this.successMsg, 'success');
        }
        if (data && data.status === 206) {
          this.popupMessageService.showSuccess(this.partialSuccessMsg, 'success');
        }
        this.getNewSalesChallengeDataList();
        this.claimAllBtn = false;
      },
      (error) => {
        if (error.message) {
          this.popupMessageService.showError(error.message, 'error');
        }
        this.claimAllBtn = false;
      }
    );
  }

  geteligibledealsData(data: any, dealId: any) {
    const promise = new Promise((resolve, reject) => {
      const reqParam: any = dealId + '?userId=' + data.id;
      this.sellerService.getChallengeDetails(reqParam).subscribe(
        (response) => {
          if (response) {
            if (response.eligibleChallenges.length > 0) {
              this.claimEligibleDeal = true;
            } else {
              this.claimEligibleDeal = false;
            }
          }
          resolve(response);
        },
        (error) => {
          console.log(error);
        }
      );
    });
    return promise;
  }

  claimedStatus(data: any) {
    if (data.claimed) {
      return 'Claimed';
    } else {
      return 'Not claimed';
    }
  }

  // ---------------- Claimed-by display helpers ----------------

  getClaimedUser(claimedUsers: any[]) {
    if (claimedUsers && claimedUsers.length) {
      return claimedUsers.map((item: any) => item.firstName + ' ' + item.lastName).join(', ');
    }
    return '';
  }

  getClaimedUsersList(claimedUsers: any[]): any[] {
    if (!claimedUsers || !claimedUsers.length) {
      return [];
    }
    return claimedUsers.map((item: any) => ({
      name: item.firstName + ' ' + item.lastName
    }));
  }

  getUserInitial(name: string): string {
    if (!name) { return '?'; }
    return name.charAt(0).toUpperCase();
  }

  isUnclaimed(element: any): boolean {
    return !element || !element.claimedUsers || element.claimedUsers.length === 0;
  }

  hasPartialClaim(element: any): boolean {
    return element && element.claimedUsers && element.claimedUsers.length === 1;
  }

  isFullyClaimed(element: any): boolean {
    return element && element.claimedUsers && element.claimedUsers.length >= 2;
  }

  // ---------------- Export ----------------

  downloadSalesClaimByExcel() {
    const url: string = sellerApi.newSalesChallenge + '/' + this.userProfile.company.id + `?page=0&size=100000&onlyEligibleDeals=${this.isEligibleDeals()}&excel=true`;
    const fileName: string = 'Sales_Challenges_Claims_' + this.userProfile.company.id + '.xlsx';
    this.fileService.excelDownloadFile(url, fileName);
  }

  getClaimTooltip() {
    return this.disableClaim
      ? 'Claiming is disabled and you won’t be able to earn rewards as minimum PVI requirement is not met.'
      : null;
  }
}
